import React, { useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCoins, FaCreditCard, FaExchangeAlt } from 'react-icons/fa';

/**
 * PaymentMethodIndicator
 * Displays a visual indicator of payment method type with tooltip details.
 *
 * Props:
 *   paymentMethod   – string: 'Cash', 'Card', 'Bank Transfer', 'Nequi', 'Daviplata', 'Multiple', etc.
 *   payments        – array: sale_payments objects with { payment_method, amount } (optional, for detailed tooltip)
 *   t               – i18n translate function (optional)
 */

/**
 * Convert a payment method string to camelCase for i18n key lookup.
 * E.g., 'Bank Transfer' → 'bankTransfer', 'Cash' → 'cash'
 */
const toI18nKey = (method) =>
  (method || '').toLowerCase().replace(/\s+(.)/g, (_, c) => c.toUpperCase());

const PaymentMethodIndicator = ({ paymentMethod, payments = [], t }) => {
  const tooltipId = useMemo(() => `payment-indicator-${Math.random().toString(36).substr(2, 9)}`, []);
  // Determine icon and color based on payment method
  const getIcon = () => {
    const method = paymentMethod?.toLowerCase() || '';

    // Cash methods
    if (method === 'cash') {
      return {
        icon: <FaCoins size={20} />,
        color: '#28a745', // green
        className: 'text-success'
      };
    }

    // Digital methods (all payment methods that are NOT cash)
    const digitalMethods = ['card', 'nequi', 'daviplata', 'bank transfer', 'bank', 'transfer', 'digital', 'electronic', 'other'];
    if (digitalMethods.some(m => method.includes(m))) {
      return {
        icon: <FaCreditCard size={20} />,
        color: '#007bff', // blue
        className: 'text-primary'
      };
    }

    // Multiple payment methods
    if (method === 'multiple') {
      return {
        icon: <FaExchangeAlt size={20} />,
        color: '#fd7e14', // orange
        className: 'text-warning'
      };
    }

    // Fallback for unknown methods
    return {
      icon: <FaCreditCard size={20} />,
      color: '#6c757d', // gray
      className: 'text-muted'
    };
  };

  // Build tooltip content
  const buildTooltipContent = () => {
    if (paymentMethod?.toLowerCase() === 'multiple' && Array.isArray(payments) && payments.length > 0) {
      // Multiple payment methods - show breakdown
      const methodGroups = {};
      let totalByMethod = {};

      payments.forEach(payment => {
        const method = payment.payment_method || 'Unknown';
        totalByMethod[method] = (totalByMethod[method] || 0) + parseFloat(payment.amount || 0);
      });

      const formatLabel = (method) => {
        if (t) {
          const key = `salesPage.paymentMethods.${toI18nKey(method)}`;
          return t(key, method);
        }
        return method;
      };

      return (
        <>
          {Object.entries(totalByMethod).map(([method, amount]) => (
            <div key={method} style={{ fontSize: '0.9rem', marginBottom: '4px' }}>
              <strong>{formatLabel(method)}:</strong> ${amount.toFixed(2)}
            </div>
          ))}
        </>
      );
    } else {
      // Single method - show method name and total if available
      const totalAmount = Array.isArray(payments)
        ? payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        : 0;

      const formatLabel = (method) => {
        if (t) {
          const key = `salesPage.paymentMethods.${toI18nKey(method)}`;
          return t(key, method);
        }
        return method;
      };

      return (
        <div style={{ fontSize: '0.9rem' }}>
          <strong>{formatLabel(paymentMethod)}</strong>
          {totalAmount > 0 && <div>${totalAmount.toFixed(2)}</div>}
        </div>
      );
    }
  };

  const { icon, className } = getIcon();

  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 200, hide: 100 }}
      overlay={
        <Tooltip id={tooltipId} className="payment-method-tooltip">
          {buildTooltipContent()}
        </Tooltip>
      }
    >
      <span className={`payment-method-indicator ${className}`} style={{ cursor: 'pointer', display: 'inline-block' }}>
        {icon}
      </span>
    </OverlayTrigger>
  );
};

export default PaymentMethodIndicator;
